<template>
  <div class="hero-stats-tab">
    <HeroTagChip
      block
      :name="sheet.identity.name"
      :meta="identityMeta"
      :image="identityCard?.image || ''"
      :image-position="identityCard?.image_position"
      :rarity="identityCard?.rarity || 'common'"
    />

    <section class="stat-plaque">
      <h3>Base Stats</h3>
      <p v-if="!identityCard" class="empty-copy">
        Socket a hero card on the Hero node to load base stats and abilities.
      </p>
      <template v-else>
        <div class="stat-grid">
          <div
            v-for="field in combatFields"
            :key="'base-' + field.key"
            class="stat-cell"
          >
            <span class="stat-key">{{ field.label }}</span>
            <span class="stat-val">{{ formatValue(field.key, sheet.base.stats[field.key]) }}</span>
          </div>
        </div>
        <div v-if="sheet.base.abilities.length" class="tag-list">
          <span
            v-for="ability in sheet.base.abilities"
            :key="ability.key"
            class="ability-tag"
            :title="ability.description"
          >
            <em v-if="ability.label">{{ ability.label }}</em>
            {{ ability.name }}
          </span>
        </div>
      </template>
    </section>

    <section class="stat-plaque">
      <h3>Equipment Bonuses</h3>
      <p v-if="!sheet.equipment.pieces.length" class="empty-copy">
        No equipment socketed. Unlock a slot and equip a card to add bonuses.
      </p>
      <div v-else class="piece-list">
        <div
          v-for="piece in sheet.equipment.pieces"
          :key="piece.nodeKey"
          class="piece-row"
        >
          <HeroTagChip
            block
            :name="piece.card?.name || piece.label"
            :meta="equipmentMeta(piece)"
            :image="piece.card?.image || ''"
            :image-position="piece.card?.image_position"
            :rarity="piece.card?.rarity || 'common'"
          />
          <div v-if="piece.affixes.length" class="tag-list">
            <span
              v-for="affix in piece.affixes"
              :key="piece.nodeKey + '-' + affix.name"
              class="ability-tag affix-tag"
              :title="affix.effect"
            >
              {{ affix.name }}
            </span>
          </div>
        </div>
      </div>
    </section>

    <section class="stat-plaque">
      <h3>Passive Node Bonuses</h3>
      <p v-if="!sheet.passives.nodes.length" class="empty-copy">
        No passive nodes unlocked yet.
      </p>
      <div v-else class="piece-list">
        <div
          v-for="node in sheet.passives.nodes"
          :key="node.nodeKey"
          class="piece-row"
        >
          <HeroTagChip
            block
            :name="node.label"
            :meta="passiveMeta(node)"
          />
          <div v-if="node.abilities.length" class="tag-list">
            <span
              v-for="ability in node.abilities"
              :key="node.nodeKey + '-' + ability.key"
              class="ability-tag"
              :title="ability.description"
            >
              {{ ability.name }}
            </span>
          </div>
        </div>
      </div>
    </section>

    <section class="stat-plaque grand-total">
      <h3>Grand Total</h3>
      <div class="total-display">
        <div
          v-for="field in combatFields"
          :key="'total-' + field.key"
          class="total-cell"
        >
          <span class="total-label">{{ field.label }}</span>
          <span class="total-value">{{ formatValue(field.key, sheet.total.stats[field.key]) }}</span>
        </div>
      </div>
      <div v-if="extraEntries.length" class="tag-list extras">
        <span
          v-for="row in extraEntries"
          :key="'extra-' + row.key"
          class="ability-tag extra-tag"
        >
          {{ row.label }} {{ row.value }}
        </span>
      </div>
    </section>
  </div>
</template>

<script setup>
import { computed } from "vue";
import { useGameStore } from "../store/game";
import HeroTagChip from "./HeroTagChip.vue";
import {
  COMBAT_STAT_FIELDS,
  compactStatSummary,
  formatCombatValue,
  titleCaseStatKey,
} from "../utils/heroCombatStats";

const gameStore = useGameStore();
const combatFields = COMBAT_STAT_FIELDS;

const sheet = computed(() => gameStore.activeHeroCombatSheet);
const identityCard = computed(() => sheet.value.identity.card);

const identityMeta = computed(() => {
  const { level, experience } = sheet.value.identity;
  return `Hero · Level ${level || 1} · XP ${experience || 0}`;
});

const extraEntries = computed(() =>
  Object.entries(sheet.value.total.extras || {}).map(([key, amount]) => ({
    key,
    label: titleCaseStatKey(key),
    value: amount > 0 ? `+${amount}` : String(amount),
  }))
);

function formatValue(key, amount) {
  return formatCombatValue(key, amount);
}

function extrasSummary(extras) {
  return Object.entries(extras || {})
    .filter(([, amount]) => Number(amount) !== 0)
    .map(([key, amount]) => {
      const n = Number(amount) || 0;
      const sign = n > 0 ? "+" : "";
      return `${sign}${n} ${titleCaseStatKey(key)}`;
    })
    .join(" · ");
}

function equipmentMeta(piece) {
  const slot = titleCaseStatKey(piece.slot);
  const summary = compactStatSummary(piece.stats);
  const extras = extrasSummary(piece.extras);
  return [slot, summary, extras].filter(Boolean).join(" · ");
}

function passiveMeta(node) {
  const summary = compactStatSummary(node.stats);
  const extras = extrasSummary(node.extras);
  return [summary, extras].filter(Boolean).join(" · ") || "Passive bonus";
}
</script>

<style scoped>
.hero-stats-tab {
  display: flex;
  flex-direction: column;
  gap: 1rem;
}

.stat-plaque {
  background: rgba(10, 15, 30, 0.95);
  border: 1px solid #00f0ff;
  border-radius: 8px;
  overflow: hidden;
  box-shadow: 0 0 10px rgba(0, 240, 255, 0.1);
  padding: 0.95rem 1rem 1.1rem;
}

.stat-plaque h3 {
  margin: 0 0 0.75rem;
  font-family: "Cinzel", serif;
  font-size: 0.78rem;
  letter-spacing: 0.12em;
  text-transform: uppercase;
  color: #c9b48a;
}

.stat-plaque.grand-total {
  background: rgba(0, 5, 15, 0.85);
  box-shadow: 0 0 14px rgba(0, 240, 255, 0.18);
}

.empty-copy {
  margin: 0;
  color: #8892b0;
  font-size: 0.8rem;
  font-style: italic;
}

.stat-grid {
  display: grid;
  grid-template-columns: repeat(5, minmax(0, 1fr));
  gap: 0.55rem;
}

.stat-cell {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 0.25rem;
  padding: 0.5rem 0.2rem;
  background: rgba(0, 240, 255, 0.06);
  border: 1px solid rgba(0, 240, 255, 0.18);
  border-radius: 4px;
  min-width: 0;
  box-sizing: border-box;
}

.stat-key {
  font-size: 0.62rem;
  letter-spacing: 0.06em;
  text-transform: uppercase;
  color: #88c0d0;
  line-height: 1.2;
}

.stat-val {
  font-weight: 700;
  color: #fff;
  font-size: 0.88rem;
  line-height: 1.2;
}

.piece-list {
  display: flex;
  flex-direction: column;
  gap: 0.9rem;
}

.piece-row {
  display: flex;
  flex-direction: column;
  gap: 0.55rem;
}

.tag-list {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
  margin-top: 0.8rem;
}

.ability-tag {
  display: flex;
  align-items: baseline;
  gap: 0.45rem;
  box-sizing: border-box;
  width: 100%;
  background: rgba(0, 240, 255, 0.1);
  border: 1px solid rgba(0, 240, 255, 0.3);
  padding: 0.45rem 0.65rem;
  border-radius: 4px;
  font-size: 0.8rem;
  line-height: 1.35;
  color: #d8dee9;
}

.ability-tag em {
  flex-shrink: 0;
  font-style: normal;
  color: #88c0d0;
  margin-right: 0;
  text-transform: uppercase;
  font-size: 0.65rem;
  letter-spacing: 0.04em;
  line-height: 1.35;
}

.affix-tag {
  border-color: rgba(235, 203, 139, 0.45);
  background: rgba(235, 203, 139, 0.12);
  color: #ebcb8b;
}

.extra-tag {
  border-color: rgba(163, 190, 140, 0.45);
  background: rgba(163, 190, 140, 0.12);
  color: #a3be8c;
}

.total-display {
  display: grid;
  grid-template-columns: repeat(5, minmax(0, 1fr));
  gap: 0.55rem;
  padding: 0.65rem 0.5rem;
  border-radius: 4px;
  background: rgba(0, 0, 0, 0.5);
  border: 1px solid #333;
}

.total-cell {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 0.25rem;
  min-width: 0;
}

.total-label {
  font-size: 0.65rem;
  text-transform: uppercase;
  letter-spacing: 0.08em;
  color: #888;
}

.total-value {
  font-size: 1.15rem;
  font-weight: 800;
  color: #00f0ff;
  text-shadow: 0 0 8px rgba(0, 240, 255, 0.35);
}

.tag-list.extras {
  margin-top: 0.8rem;
}
</style>
